import React from 'react';
import { WizardState } from '../types';

interface ReviewStepProps {
  state: WizardState;
}

const ReviewStep: React.FC<ReviewStepProps> = ({ state }) => {
  const { merchant, subaccounts, agents } = state;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900">Review & Confirm</h3>
        <p className="mt-1 text-sm text-gray-500">
          Please review the information before submitting.
        </p>
      </div>

      {/* Merchant Summary */}
      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
          </div>
          <h4 className="text-base font-semibold text-gray-900">Merchant</h4>
        </div>

        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <dt className="text-sm font-medium text-gray-500">Name</dt>
            <dd className="mt-1 text-sm text-gray-900">{merchant.name}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Type</dt>
            <dd className="mt-1 text-sm text-gray-900">
              <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                merchant.type === 'agent'
                  ? 'bg-purple-100 text-purple-800'
                  : 'bg-blue-100 text-blue-800'
              }`}>
                {merchant.type === 'agent' ? 'Agent Merchant' : 'Regular Merchant'}
              </span>
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Description</dt>
            <dd className="mt-1 text-sm text-gray-900">{merchant.description}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Batch Postback</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {merchant.isBatchPostback === 'yes' ? 'Enabled' : 'Disabled'}
            </dd>
          </div>
          {merchant.restrictedIps && (
            <div className="sm:col-span-2">
              <dt className="text-sm font-medium text-gray-500">Restricted IPs</dt>
              <dd className="mt-1 text-sm text-gray-900">{merchant.restrictedIps}</dd>
            </div>
          )}
        </dl>
      </div>

      {/* Subaccounts Summary (for Regular Merchant) */}
      {merchant.type === 'regular' && subaccounts.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-green-100 text-green-600">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
                />
              </svg>
            </div>
            <h4 className="text-base font-semibold text-gray-900">
              Subaccounts ({subaccounts.length})
            </h4>
          </div>

          <div className="space-y-4">
            {subaccounts.map((sub, index) => (
              <div key={sub.tempId} className="rounded-lg border border-gray-100 bg-gray-50 p-4">
                <div className="flex items-center gap-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-200 text-xs font-medium text-gray-600">
                    {index + 1}
                  </span>
                  <span className="font-medium text-gray-900">{sub.name}</span>
                </div>

                <dl className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  <div>
                    <dt className="text-xs font-medium text-gray-500">Postback URL</dt>
                    <dd className="mt-0.5 text-sm text-gray-900 break-all">
                      {sub.postbackUrl || '-'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium text-gray-500">Name</dt>
                    <dd className="mt-0.5 text-sm text-gray-900">
                      {sub.firstname} {sub.lastname}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium text-gray-500">Email</dt>
                    <dd className="mt-0.5 text-sm text-gray-900">
                      {sub.email || '-'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium text-gray-500">Phone</dt>
                    <dd className="mt-0.5 text-sm text-gray-900">
                      {sub.mobile ? `+${sub.mobileDialcode} ${sub.mobile}` : '-'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium text-gray-500">Business Name</dt>
                    <dd className="mt-0.5 text-sm text-gray-900">
                      {sub.businessName || '-'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium text-gray-500">Fee Group</dt>
                    <dd className="mt-0.5 text-sm text-gray-900">
                      {sub.feeGroup.mode === 'existing'
                        ? sub.feeGroup.existingFeeGroupName || 'Selected existing'
                        : `New: ${sub.feeGroup.newFeeGroup?.name || 'Unnamed'}`}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium text-gray-500">Payment Methods</dt>
                    <dd className="mt-0.5 text-sm text-gray-900">
                      {sub.paymentMethods.length} selected
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium text-gray-500">Data Masking</dt>
                    <dd className="mt-0.5 text-sm text-gray-900">
                      {sub.isMasked === 'yes' ? 'Enabled' : 'Disabled'}
                    </dd>
                  </div>
                </dl>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Agents Summary (for Agent Merchant) */}
      {merchant.type === 'agent' && agents.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-purple-100 text-purple-600">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </div>
            <h4 className="text-base font-semibold text-gray-900">
              Agents ({agents.length})
            </h4>
          </div>

          <div className="space-y-4">
            {agents.map((agent, index) => (
              <div key={agent.tempId} className="rounded-lg border border-gray-100 bg-gray-50 p-4">
                <div className="flex items-center gap-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-200 text-xs font-medium text-gray-600">
                    {index + 1}
                  </span>
                  <span className="font-medium text-gray-900">
                    {agent.firstname} {agent.lastname}
                  </span>
                </div>

                <dl className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  <div>
                    <dt className="text-xs font-medium text-gray-500">Business Name</dt>
                    <dd className="mt-0.5 text-sm text-gray-900">
                      {agent.businessName || '-'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium text-gray-500">Email</dt>
                    <dd className="mt-0.5 text-sm text-gray-900">
                      {agent.email || '-'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium text-gray-500">Phone</dt>
                    <dd className="mt-0.5 text-sm text-gray-900">
                      {agent.mobile ? `+${agent.mobileDialcode} ${agent.mobile}` : '-'}
                    </dd>
                  </div>
                </dl>

                {agent.feeGroupAssignments.length > 0 && (
                  <div className="mt-3">
                    <dt className="text-xs font-medium text-gray-500 mb-2">
                      Fee Group Assignments ({agent.feeGroupAssignments.length})
                    </dt>
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="py-1 text-left text-xs font-medium text-gray-500">Asset</th>
                            <th className="py-1 text-left text-xs font-medium text-gray-500">Method</th>
                            <th className="py-1 text-left text-xs font-medium text-gray-500">Fee Group</th>
                          </tr>
                        </thead>
                        <tbody>
                          {agent.feeGroupAssignments.map(fg => (
                            <tr key={fg.tempId} className="border-b border-gray-100">
                              <td className="py-1 text-gray-900">{fg.asset}</td>
                              <td className="py-1 text-gray-900">{fg.methodName}</td>
                              <td className="py-1 text-gray-900">
                                {fg.feeGroup.mode === 'existing'
                                  ? fg.feeGroup.existingFeeGroupName || 'Selected'
                                  : `New: ${fg.feeGroup.newFeeGroup?.name || 'Unnamed'}`}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Submission Info */}
      <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              Ready to submit
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>
                When you click "Submit", the following will be created in sequence:
              </p>
              <ul className="mt-1 list-inside list-disc">
                <li>1 Merchant</li>
                {merchant.type === 'regular' ? (
                  (() => {
                    const newFeeGroupCount = subaccounts.filter(s => s.feeGroup.mode === 'new').length;
                    const existingFeeGroupCount = subaccounts.filter(s => s.feeGroup.mode === 'existing').length;
                    return (
                      <>
                        {newFeeGroupCount > 0 && <li>{newFeeGroupCount} new Fee Group(s)</li>}
                        {existingFeeGroupCount > 0 && <li>{existingFeeGroupCount} existing Fee Group(s)</li>}
                        <li>{subaccounts.length} Subaccount(s)</li>
                      </>
                    );
                  })()
                ) : (
                  (() => {
                    const newFeeGroupCount = agents.reduce((acc, a) => acc + a.feeGroupAssignments.filter(fg => fg.feeGroup.mode === 'new').length, 0);
                    const existingFeeGroupCount = agents.reduce((acc, a) => acc + a.feeGroupAssignments.filter(fg => fg.feeGroup.mode === 'existing').length, 0);
                    return (
                      <>
                        {newFeeGroupCount > 0 && <li>{newFeeGroupCount} new Fee Group(s)</li>}
                        {existingFeeGroupCount > 0 && <li>{existingFeeGroupCount} existing Fee Group(s)</li>}
                        <li>{agents.length} Agent(s)</li>
                        <li>{agents.reduce((acc, a) => acc + a.feeGroupAssignments.length, 0)} Fee Group Assignment(s)</li>
                      </>
                    );
                  })()
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewStep;
